import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiService } from '@app/core/http/api.service';
import { IOcbApiPrivateKeyParameter, IOcbApiStringParameter, IOcbCPgpKey, IOcbPgpKey, IOcbUPgpKey } from '@app/core/models';
import { environment as env } from '@env/environment';
import { Observable } from 'rxjs';

@Injectable()
export class PgpKeysService extends ApiService {

    private pgpKeysUrl: string;

    private options = {
        headers: this.defaultHeaders,
        observe: 'body' as 'body',
    };

    constructor(httpClient: HttpClient) {
        super(httpClient);

        if (!env.api.endpoint) {
            throw new Error('Could not initialize the "remote-platforms" service, the proxyPath configuration is invalid.');
        }

        this.pgpKeysUrl = `${env.api.endpoint}/pgpkeys`;
    }


    /**
    * Add a PGP key
    * @param pgpKey PGP key to create
     */
    public addPgpKey(pgpKey: IOcbCPgpKey): Observable<IOcbPgpKey> {
        return this.httpClient.post<IOcbPgpKey>(this.pgpKeysUrl, pgpKey, this.options);
    }

    /**
     * Update a PGP key
     * @param pgpKeyId Internal identifier of the PGP key
     * @param remotePlatform Data to update
     */
    public updatePgpKey(pgpKeyId: string, pgpKey: IOcbUPgpKey): Observable<void> {
        const url = `${this.pgpKeysUrl}/${pgpKeyId}`;
        return this.httpClient.put<void>(url, pgpKey, this.options);
    }


    /**
     * Change the public key of a PGP key
     * @param pgpKeyId Internal identifier of the PGP key
     * @param newPublicKey New public key
     */
    public changePublicKey(pgpKeyId: string, newPublicKey: IOcbApiStringParameter) {
        const url = `${this.pgpKeysUrl}/${pgpKeyId}/PublicKey`;
        return this.httpClient.post<void>(url, newPublicKey, this.options);
    }

    /**
    * Change the private key (and its password) of a PGP key
    * @param pgpKeyId Internal identifier of the PGP key
    * @param newPrivateKey New private key and password
    */
    public changePrivateKey(pgpKeyId: string, newPrivateKey: IOcbApiPrivateKeyParameter) {
        const url = `${this.pgpKeysUrl}/${pgpKeyId}/PrivateKey`;
        return this.httpClient.post<void>(url, newPrivateKey, this.options);
    }


    /**
    * Get a PGP key
    * @param pgpKeyId Internal identifier of the PGP key
    */
    public getPgpKey(pgpKeyId: string): Observable<IOcbPgpKey> {

        let url = `${this.pgpKeysUrl}/${pgpKeyId}`;
        return this.httpClient.get<IOcbPgpKey>(url, this.options);
    }

    /**
     * Get the list of PGP keys
     * @param environmentId Internal identifier of the environment
     * @param scope Scope of the requested keys
     */
    public getPgpKeys(environmentId?: string, scope?:string): Observable<IOcbPgpKey[]> {

        let url = `${this.pgpKeysUrl}?`;

        if (environmentId) { url = `${url}EnvironmentId=${environmentId}&`; }
        if (scope) { url = `${url}Scope=${scope}&`; }

        return this.httpClient.get<IOcbPgpKey[]>(url, this.options);
    }


    /**
     * Delete a PGP key
     * @param pgpKeyId Internal identifier of the PGP key
     */
    public deletePgpKey(pgpKeyId: string): Observable<void> {
        const url = `${this.pgpKeysUrl}/${pgpKeyId}`;
        return this.httpClient.delete<void>(url, this.options);
    }

}
